/**
 * 数据管理页面
 * 展示 SKU 相关的周期数据管理
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Table,
    Button,
    Space,
    Tag,
    message,
    Popconfirm,
    Input,
    Select,
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    EyeOutlined,
    SearchOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import api from '../../services/api';


interface SKUData {
    id: number;
    name: string;
    asin: string;
    marketplace: string;
    periods_count: number;
    last_period_date?: string;
    created_at: string;
}

export const DataManagement: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<SKUData[]>([]);
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const response = await api.get('/skus/');
            const skuData = response.data?.results || response.data || [];
            setData(Array.isArray(skuData) ? skuData : []);
        } catch (error) {
            message.error('加载数据失败');
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredData = data.filter(item =>
        !searchText ||
        item.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.asin?.toLowerCase().includes(searchText.toLowerCase())
    );

    const columns: ColumnsType<SKUData> = [
        {
            title: 'SKU名称',
            dataIndex: 'name',
            key: 'name',
            ellipsis: true,
        },
        {
            title: 'ASIN',
            dataIndex: 'asin',
            key: 'asin',
            width: 130,
        },
        {
            title: '站点',
            dataIndex: 'marketplace',
            key: 'marketplace',
            width: 80,
            render: (val) => <Tag>{val || 'US'}</Tag>,
        },
        {
            title: '周期数',
            dataIndex: 'periods_count',
            key: 'periods_count',
            width: 80,
            align: 'center',
            render: (val) => val || 0,
        },
        {
            title: '最近周期',
            dataIndex: 'last_period_date',
            key: 'last_period_date',
            width: 120,
            render: (val) => val || '-',
        },
        {
            title: '操作',
            key: 'action',
            width: 120,
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => navigate(`/data-management/${record.id}`)}
                    >
                        查看
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <Card
            title="数据管理"
            extra={
                <Space>
                    <Input
                        placeholder="搜索 SKU/ASIN"
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ width: 200 }}
                        allowClear
                    />
                    <Button icon={<ReloadOutlined />} onClick={loadData}>
                        刷新
                    </Button>
                </Space>
            }
        >
            <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="id"
                loading={loading}
                pagination={{
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => `共 ${total} 条`,
                }}
            />
        </Card>
    );
};

export default DataManagement;
